import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CategoryCard from "@/components/base/CategoryCard";
import ArticleCard from "@/components/base/ArticleCard";
import FAQItem from "@/components/base/FAQItem";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { tools } from "@/mocks/tools";
import { faqs } from "@/mocks/faqs";

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "磁力搜索指南",
  url: "https://cili-sousuo.com",
  description:
    "磁力搜索是最专业的磁力搜索知识平台，提供磁力搜索技巧、工具推荐和安全指南。",
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "磁力搜索指南",
  url: "https://cili-sousuo.com",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://cili-sousuo.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
};

export default function Home() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const featuredArticles = articles.slice(0, 3);
  const latestArticles = articles.slice(3, 6);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationJsonLd),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteJsonLd),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqJsonLd),
        }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[620px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20geometric%20shapes%20and%20subtle%20digital%20network%20patterns%2C%20cream%20and%20terracotta%20tones%2C%20minimalist%20modern%20design%2C%20professional%20atmosphere%2C%20no%20text%20or%20logos&width=1920&height=900&seq=hero-bg-main&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          </div>

          <div
            className={`relative z-10 w-full px-4 md:px-6 text-center transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <span className="inline-block text-sm font-medium text-primary-300 bg-primary-500/20 backdrop-blur-sm px-4 py-1.5 rounded-full mb-6">
              最专业的磁力搜索知识平台
            </span>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 mb-6 leading-tight">
              磁力搜索
              <br />
              <span className="text-primary-300">极速找资源，一搜即得</span>
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-10 leading-relaxed">
              磁力搜索是最高效的BT资源检索方式，通过磁力链接快速定位海量影视、软件、文档资源。掌握磁力搜索技巧，让你的资源获取更快、更准、更安全。
            </p>

            <div className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-foreground-400 text-base"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="输入关键词搜索磁力搜索教程..."
                  className="w-full pl-11 pr-4 py-3.5 bg-background-50/95 backdrop-blur-sm border border-background-200/60 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all duration-200"
                />
              </div>
              <button className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap">
                <i className="ri-search-line"></i>
                搜索教程
              </button>
            </div>

            <div className="flex items-center justify-center gap-8 mt-10">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                  {articles.length}+
                </div>
                <div className="text-xs text-background-300 mt-1">
                  精品教程
                </div>
              </div>
              <div className="w-px h-8 bg-background-300/30"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                  {categories.length}
                </div>
                <div className="text-xs text-background-300 mt-1">
                  资源分类
                </div>
              </div>
              <div className="w-px h-8 bg-background-300/30"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                  {tools.length}+
                </div>
                <div className="text-xs text-background-300 mt-1">
                  推荐工具
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                磁力搜索资源分类导航
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                按资源类型浏览磁力搜索教程，覆盖影视、软件、文档、音乐、游戏等主流资源类别，助你快速找到所需内容。
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <CategoryCard
                  key={cat.slug}
                  slug={cat.slug}
                  name={cat.name}
                  description={cat.description}
                  icon={cat.icon}
                  articleCount={cat.articleCount}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                  精选教程推荐
                </h2>
                <p className="text-foreground-500 leading-relaxed">
                  精心挑选的磁力搜索核心教程，从入门到进阶
                </p>
              </div>
              <Link
                to="/category/yingshi"
                className="text-sm text-primary-500 font-medium flex items-center gap-1 hover:gap-2 transition-all duration-200 whitespace-nowrap"
              >
                查看全部教程
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Tools Recommendation */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                磁力搜索必备工具推荐
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                选择合适的下载工具能让磁力搜索体验事半功倍，以下是经过实测验证的优秀BT下载客户端。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {tools.map((tool) => (
                <div
                  key={tool.name}
                  className="p-6 bg-background-50 border border-background-200/70 rounded-xl hover:border-accent-200/70 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100/80 flex items-center justify-center flex-shrink-0">
                      <i className="ri-tools-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
                      {tool.name}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                    {tool.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tool.features.map((feature) => (
                      <span
                        key={feature}
                        className="text-xs text-accent-700 bg-accent-100/70 px-2.5 py-1 rounded-full whitespace-nowrap"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                  最新磁力搜索教程
                </h2>
                <p className="text-foreground-500 leading-relaxed">
                  持续更新的磁力搜索技巧与资源获取指南
                </p>
              </div>
              <Link
                to="/category/ruanjian"
                className="text-sm text-primary-500 font-medium flex items-center gap-1 hover:gap-2 transition-all duration-200 whitespace-nowrap"
              >
                更多教程
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                磁力搜索常见问题
              </h2>
              <p className="text-foreground-500 leading-relaxed">
                关于磁力搜索的常见疑问，这里都有详细解答
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  defaultOpen={index === 0}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20texture%20with%20soft%20geometric%20patterns%20in%20terracotta%20and%20cream%20tones%2C%20minimalist%20modern%20background%2C%20professional%20aesthetic%2C%20no%20text&width=1920&height=600&seq=cta-bg-home&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-800/85"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-5">
              准备好开始磁力搜索之旅了吗？
            </h2>
            <p className="text-background-200 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
              浏览我们的磁力搜索教程，掌握最实用的搜索技巧，让资源获取更高效。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/yingshi"
                className="px-8 py-3.5 bg-background-50 text-primary-700 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
              >
                浏览教程
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 bg-primary-400/30 text-background-50 border border-background-50/30 rounded-full text-sm font-semibold hover:bg-primary-400/40 transition-all duration-200 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}